import { Component, OnInit } from '@angular/core';
import { HttpClient, HttpHeaders, HttpParams } from '@angular/common/http';
import { AuthService } from '../services/auth.service';
import { ImportedTargetDefinition } from '../imported-target.model';
import { parseImportedTargetXml } from '../imported-target.parser';
@Component({
  selector: 'app-settings',
  templateUrl: './settings.component.html',
  styleUrls: ['./settings.component.scss']
})
export class SettingsComponent implements OnInit {
  private readonly importedTargetXmlKey = 'tarce.importedTargetXml';
  private readonly importedTargetDefinitionKey = 'tarce.importedTargetDefinition';
  oldPassword: string = '';
  newPassword: string = '';
  newEmail: string = '';
  username: string = '';
  infoOpen: boolean = false;
  infoTitle: string = 'Napaka';
  infoMessage: string = '';
  isMobile = window.innerWidth < 840;
  xmlFileName = '';
  xmlFileNames: string[] = [];
  xmlRootName = '';
  xmlStatus = '';
  xmlError = '';
  parsedTargetXml: Document | null = null;
  importedTargetDefinition: ImportedTargetDefinition | null = null;
  savedXmlTargets: any[] = [];
  selectedSavedXmlTargetId: number | null = null;
  private processedXmlFiles = 0;
  private successfulXmlFiles = 0;
  private failedXmlFiles: string[] = [];

  constructor(
    private http: HttpClient,
    private authService: AuthService
  ) {}

  ngOnInit() {
    this.authService.checkToken().subscribe();
    this.loadSavedXmlTargets();

    const username = localStorage.getItem('username');
    const unit_id = localStorage.getItem('unit_id');
    const token = localStorage.getItem('access_token');

    if (username && unit_id && token) {
      this.username = username;

      const headers = new HttpHeaders({ Authorization: `Bearer ${token}` });

      this.http.get<any>(this.authService.apiUrl + '/api/strelstvo/get-user-data', {
        params: { username, UnitID: unit_id },
        headers
      }).subscribe({
        next: (response) => {
          if (response.email) {
            this.newEmail = response.email;
          }
          if (response.username) {
            this.username = response.username;
          }
        },
        error: (error) => {
          console.error('Error loading user data:', error);
          // Leave fields empty if error
          this.newEmail = '';
        }
      });
    }
  }

  onChangePassword(): void {
    const username = this.username;
    const token = localStorage.getItem('access_token');

    if (!username) {
      this.infoTitle = 'Napaka';
      this.infoMessage = 'Uporabniško ime ni na voljo.';
      this.infoOpen = true;
      return;
    }

    if (!token) {
      this.infoTitle = 'Napaka';
      this.infoMessage = 'Manjka avtentikacijski žeton.';
      this.infoOpen = true;
      return;
    }

    // Check if user wants to change password but didn't provide new password
    if (this.oldPassword && !this.newPassword && !this.newEmail) {
      this.infoTitle = 'Napaka';
      this.infoMessage = 'Vnesite novo geslo ali nov email.';
      this.infoOpen = true;
      return;
    }

    // If changing email only, old password is not required
    const isEmailOnlyChange = this.newEmail && !this.newPassword;

    // If changing password, old password and new password are required
    if (this.newPassword && !this.oldPassword) {
      this.infoTitle = 'Napaka';
      this.infoMessage = 'Za spremembo gesla je potrebno staro geslo.';
      this.infoOpen = true;
      return;
    }

    const body = new HttpParams()
      .set('username', username)
      .set('old_password', this.oldPassword)
      .set('new_password', this.newPassword || '')
      .set('new_email', this.newEmail || '');

    const headers = new HttpHeaders({
      'Content-Type': 'application/x-www-form-urlencoded',
      Authorization: `Bearer ${token}`
    });

    this.http.put<any>(this.authService.apiUrl + '/api/strelstvo/change-user-data', body, {
      headers: headers
    }).subscribe({
      next: (response) => {
        this.infoTitle = 'Uspeh';
        this.infoMessage = response.message || 'Sprememba uspešna.';
        this.infoOpen = true;
        this.oldPassword = '';
        this.newPassword = '';
        // Do not clear newEmail, keep it as user's email
      },
      error: (error) => {
        console.error('Error changing user data:', error);
        this.infoTitle = 'Napaka';
        this.infoMessage = error.error?.detail || 'Napaka pri spremembi podatkov.';
        this.infoOpen = true;
      }
    });
  }

  onXmlFileSelected(event: Event): void {
    this.xmlError = '';
    this.xmlStatus = '';
    this.xmlRootName = '';
    this.parsedTargetXml = null;
    this.importedTargetDefinition = null;
    this.xmlFileNames = [];
    this.processedXmlFiles = 0;
    this.successfulXmlFiles = 0;
    this.failedXmlFiles = [];

    const input = event.target as HTMLInputElement;
    const files = Array.from(input.files || []);
    if (!files.length) return;

    const invalidFiles = files.filter(file => !this.isXmlFile(file));
    const xmlFiles = files.filter(file => this.isXmlFile(file));
    if (invalidFiles.length) {
      this.failedXmlFiles.push(...invalidFiles.map(file => file.name));
    }
    if (!xmlFiles.length) {
      this.xmlError = 'Izberite eno ali več datotek XML.';
      input.value = '';
      return;
    }

    this.xmlFileNames = xmlFiles.map(file => file.name);
    this.xmlFileName = this.xmlFileNames.join(', ');
    xmlFiles.forEach(file => this.readXmlFile(file));
  }

  private isXmlFile(file: File): boolean {
    return file.name.toLowerCase().endsWith('.xml') ||
      file.type === 'text/xml' ||
      file.type === 'application/xml';
  }

  private readXmlFile(file: File): void {
    const reader = new FileReader();
    reader.onload = () => {
      this.processedXmlFiles++;
      try {
        if (this.parseTargetXml(String(reader.result || ''))) {
          this.successfulXmlFiles++;
        } else {
          this.failedXmlFiles.push(file.name);
        }
      } catch (error) {
        this.failedXmlFiles.push(file.name);
      }
      this.updateXmlImportStatus();
    };
    reader.onerror = () => {
      this.processedXmlFiles++;
      this.failedXmlFiles.push(file.name);
      this.updateXmlImportStatus();
    };
    reader.readAsText(file);
  }

  private updateXmlImportStatus(): void {
    const totalFiles = this.xmlFileNames.length;
    if (this.processedXmlFiles < totalFiles) {
      this.xmlStatus = `Obdelanih ${this.processedXmlFiles} od ${totalFiles} XML datotek.`;
      return;
    }

    this.xmlStatus = `Uspešno pripravljeno XML tarč: ${this.successfulXmlFiles} od ${totalFiles}.`;
    if (this.failedXmlFiles.length) {
      this.xmlError = `Ni bilo mogoče uvoziti: ${this.failedXmlFiles.join(', ')}.`;
    }
  }

  private parseTargetXml(xmlText: string): boolean {
    try {
      const documentParser = new DOMParser();
      const document = documentParser.parseFromString(xmlText, 'application/xml');
      this.importedTargetDefinition = parseImportedTargetXml(xmlText);
      this.parsedTargetXml = document;
      this.xmlRootName = document.documentElement.nodeName;
      localStorage.setItem(this.importedTargetXmlKey, xmlText);
      localStorage.setItem(
        this.importedTargetDefinitionKey,
        JSON.stringify(this.importedTargetDefinition)
      );
      this.xmlStatus = `XML tarča "${this.importedTargetDefinition.displayName}" je uspešno pripravljena.`;
      this.saveXmlTargetToServer(this.importedTargetDefinition, xmlText);
      return true;
    } catch (error) {
      this.xmlError = error instanceof Error ? error.message : 'Datoteka XML ni veljavna.';
      this.xmlStatus = '';
      this.parsedTargetXml = null;
      this.importedTargetDefinition = null;
      return false;
    }
  }

  private loadSavedXmlTargets(): void {
    const token = localStorage.getItem('access_token');
    const unitId = localStorage.getItem('unit_id');
    if (!token || !unitId) return;
    this.http.get<any[]>(this.authService.apiUrl + '/api/strelstvo/xml-targets', {
      headers: new HttpHeaders({ Authorization: `Bearer ${token}` }),
      params: new HttpParams().set('unit_id', unitId)
    }).subscribe({
      next: targets => this.savedXmlTargets = Array.isArray(targets) ? targets : [],
      error: error => console.error('Error loading saved XML targets:', error)
    });
  }

  private saveXmlTargetToServer(definition: ImportedTargetDefinition, xmlText: string): void {
    const token = localStorage.getItem('access_token');
    const unitId = localStorage.getItem('unit_id');
    if (!token || !unitId) {
      this.xmlError = 'Za shranjevanje XML tarče se morate prijaviti.';
      return;
    }
    this.http.post<any>(this.authService.apiUrl + '/api/strelstvo/xml-targets', {
      display_name: definition.displayName,
      target_name: definition.targetName,
      profile: definition.profile,
      xml_text: xmlText,
      definition
    }, {
      headers: new HttpHeaders({ Authorization: `Bearer ${token}` }),
      params: new HttpParams().set('unit_id', unitId)
    }).subscribe({
      next: saved => {
        this.savedXmlTargets = [
          ...this.savedXmlTargets.filter(target => target.id !== saved.id),
          saved
        ].sort((left, right) => left.display_name.localeCompare(right.display_name));
        this.selectedSavedXmlTargetId = saved.id;
        this.xmlStatus = `XML tarča "${definition.displayName}" je shranjena in pripravljena za izbiro.`;
      },
      error: error => {
        console.error('Error saving XML target:', error);
        this.xmlError = 'XML tarče ni bilo mogoče shraniti v bazo.';
      }
    });
  }

  selectSavedXmlTarget(target: any): void {
    if (!target?.definition) return;
    this.importedTargetDefinition = target.definition;
    this.selectedSavedXmlTargetId = target.id;
    this.xmlStatus = `Izbrana XML tarča: ${target.display_name}.`;
    localStorage.setItem(this.importedTargetDefinitionKey, JSON.stringify(target.definition));
  }

  selectSavedXmlTargetById(rawId: string): void {
    const target = this.savedXmlTargets.find(item => String(item.id) === String(rawId));
    if (target) this.selectSavedXmlTarget(target);
  }

  clearXmlImport(): void {
    this.xmlFileName = '';
    this.xmlFileNames = [];
    this.xmlRootName = '';
    this.xmlStatus = '';
    this.xmlError = '';
    this.parsedTargetXml = null;
    this.importedTargetDefinition = null;
    localStorage.removeItem(this.importedTargetXmlKey);
    localStorage.removeItem(this.importedTargetDefinitionKey);
  }
}